import React, { createContext, useEffect, useMemo, useCallback } from 'react'
import { useAppDispatch } from 'hooks/reduxHooks'
import { selectLoggedIn } from 'selectors/accountSelectors'
import useReduxStateListener from 'hooks/useReduxStateListener'
import { accountAccessShowLogin } from 'actions/UiActions'
import useSocket, { SocketType } from '../hooks/useSocket'

interface LoginListenerState {
  loggedIn: boolean
  account?: App.AuthAccount
}

export const SocketContext = createContext<{
  socket: SocketType | undefined
  onLogin: () => Promise<LoginListenerState>
}>({
  socket: undefined,
  onLogin: () => new Promise(() => {}),
})

function SocketContextProvider({ children }: React.PropsWithChildren) {
  const dispatch = useAppDispatch()

  const { socket, resetSocket } = useSocket()

  const loginListener = useReduxStateListener<LoginListenerState>((state) => {
    if (selectLoggedIn(state)) {
      return {
        loggedIn: true,
        account: state.auth.account,
      }
    } else if (!state.ui.modal.open) {
      return { loggedIn: false }
    }
  })

  const onLogin = useCallback(async(): Promise<LoginListenerState> => {
    dispatch(accountAccessShowLogin())
    const { loggedIn, account } = await loginListener.awaitResult()

    if (loggedIn) {
      await resetSocket()
    }

    return {
      loggedIn,
      account,
    }
  }, [dispatch, loginListener, resetSocket])

  useEffect(() => {
    if (socket) {
      socket.connect()
    }
  }, [socket])

  const contextValue = useMemo(() => {
    return {
      socket,
      onLogin,
    }
  }, [socket, onLogin])

  return (
    <SocketContext.Provider value={contextValue}>
      {children}
    </SocketContext.Provider>
  )
}

export default SocketContextProvider
